import React from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  IconButton,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { Leaf } from 'lucide-react';
import { formatDateBr as formatDateBrGlobal } from '../../../utils/formatters/dateUtils';
import { classifyReservaLegalStatus } from '../../../utils/reportCalculations';
import { useReportConfig } from '../../../contexts/ReportConfigContext.jsx';
import ReportStyledField from '../ReportStyledField.jsx';

export default function CarDomain({ data, sobreposicao }) {
  const { isDomainEnabled, isSectionEnabled, isFieldEnabled, getFieldLabel, getStyleForRole } =
    useReportConfig();

  if (!isDomainEnabled('car')) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>
          CAR
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Domínio CAR desabilitado na configuração do relatório.
        </Typography>
      </Box>
    );
  }

  if (!Array.isArray(data) || data.length === 0) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>
          CAR
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Nenhum registro de CAR encontrado.
        </Typography>
      </Box>
    );
  }

  const safeNumber = (value) => {
    if (value === null || value === undefined || value === '') return null;
    const n = Number(value);
    return Number.isFinite(n) ? n : null;
  };

  const statusMap = {
    AT: 'Ativo',
    PE: 'Pendente',
    CA: 'Cancelado',
  };

  const tipoImovelMap = {
    IRU: 'Imóvel Rural',
    AST: 'Assentamento INCRA',
    PCT: 'Povos e Comunidades Tradicionais',
  };

  const formatStatusLabel = (code) => {
    if (!code) return '-';
    const label = statusMap[code];
    if (label) return label;
    return code;
  };

  const formatTipoImovelLabel = (code) => {
    if (!code) return '-';
    const label = tipoImovelMap[code];
    if (label) return label;
    return code;
  };

  const getStatusChipProps = (code) => {
    const label = formatStatusLabel(code);
    switch (code) {
      case 'AT':
        return { label, color: 'success' };
      case 'PE':
        return { label, color: 'warning' };
      case 'CA':
        return { label, color: 'error' };
      default:
        return { label: label || '-', color: 'default' };
    }
  };

  const formatArea = (value) => {
    const n = safeNumber(value);
    if (n === null) return '';
    return n.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  const computePercent = (areaTema, areaPropriedade) => {
    const tema = safeNumber(areaTema);
    const total = safeNumber(areaPropriedade);
    if (tema === null || total === null || total === 0) return '';
    const pct = (tema / total) * 100;
    return `${pct.toLocaleString('pt-BR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })} %`;
  };

  const formatBiomaChipLabel = (bioma, pct) => {
    if (!bioma) return '-';
    const hasPct = pct !== null && pct !== undefined && pct !== '';
    if (!hasPct) return bioma;
    return `${bioma} (${formatPercentValue(pct)})`;
  };

  const formatDateBr = (value) => {
    if (value === null || value === undefined || value === '') return '-';
    const formatted = formatDateBrGlobal(value);
    return formatted || String(value);
  };

  const leftThemes = [
    { label: 'Área Líquida', field: 'area_liquida' },
    { label: 'Área de Preservação Permanente', field: 'area_de_preservacao_permanente' },
    { label: 'Área de Reserva Legal Proposta', field: 'area_de_reserva_legal_proposta' },
    { label: 'Área de Reserva Legal Averbada', field: 'area_de_reserva_legal_averbada' },
    {
      label: 'Área de Reserva Legal não Averbada',
      field: 'area_de_reserva_legal_aprovada_e_nao_averbada',
    },
    { label: 'Área de Vegetação Nativa', field: 'area_remanescente_de_vegetacao_nativa' },
    { label: 'Área de Uso Restrito', field: 'area_de_uso_restrito' },
  ];

  const rightThemes = [
    { label: 'Área Não Classificada', field: 'area_nao_classificada' },
    { label: 'Área de Servidão Administrativa', field: 'area_de_servidao_administrativa' },
    { label: 'Área Rural Consolidada', field: 'area_rural_consolidada' },
    { label: 'Área de Pousio', field: 'area_de_pousio' },
  ];

  const visibleLeftThemes = leftThemes.filter((theme) =>
    isFieldEnabled('car', 'temas_ambientais', theme.field),
  );
  const visibleRightThemes = rightThemes.filter((theme) =>
    isFieldEnabled('car', 'temas_ambientais', theme.field),
  );

  const formatPercentValue = (value) => {
    const n = safeNumber(value);
    if (n === null) return '';
    return `${n.toLocaleString('pt-BR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })} %`;
  };

  const extractBiomas = (sobreposicaoData) => {
    if (!sobreposicaoData) return [];

    const array = Array.isArray(sobreposicaoData) ? sobreposicaoData : [sobreposicaoData];

    const collected = [];

    array.forEach((item) => {
      const biomas = Array.isArray(item?.temas?.biomas) ? item.temas.biomas : [];
      biomas.forEach((b) => {
        const nome = b?.bioma;
        const pct = b?.pct_imovel;
        if (nome) {
          collected.push({ bioma: nome, pct });
        }
      });
    });

    const map = new Map();
    collected.forEach((b) => {
      const key = `${b.bioma}-${b.pct ?? ''}`;
      if (!map.has(key)) {
        map.set(key, b);
      }
    });

    // Garantir no máximo 3 biomas distintos
    return Array.from(map.values()).slice(0, 3);
  };

  const biomasChips = extractBiomas(sobreposicao);

  const renderField = (domainKey, fieldKey, label, value) => (
    <Box sx={{ mb: 1.5 }}>
      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
        {label}
      </Typography>
      <ReportStyledField
        domainKey={domainKey}
        fieldKey={fieldKey}
        variant="body1"
        defaultSx={{ fontWeight: 500 }}
      >
        {value || '-'}
      </ReportStyledField>
    </Box>
  );
  const renderThemesTable = (themes, details, areaPropriedade) => (
    <Table
      size="small"
      sx={{
        backgroundColor: 'white',
      }}
    >
      <TableHead>
        <TableRow>
          <TableCell sx={{ fontWeight: 700 }}>Temas Ambientais</TableCell>
          <TableCell align="right" sx={{ fontWeight: 700 }}>
            ha
          </TableCell>
          <TableCell align="right" sx={{ fontWeight: 700 }}>
            %
          </TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {themes.map((theme, index) => {
          const areaValor = details?.[theme.field];
          const label = getFieldLabel('car', theme.field, theme.label);
          return (
            <TableRow key={theme.field} sx={{ bgcolor: index % 2 === 0 ? 'grey.50' : 'grey.100' }}>
              <TableCell>{label}</TableCell>
              <TableCell align="right">{formatArea(areaValor)}</TableCell>
              <TableCell align="right">{computePercent(areaValor, areaPropriedade)}</TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );

  return (
    <Box sx={{ p: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <Leaf size={20} style={{ marginRight: 8, color: '#16a34a' }} />
        <Typography variant="h6" sx={getStyleForRole('domainTitle', {})}>
          Cadastro Ambiental Rural (CAR)
        </Typography>
      </Box>

      {data.map((car, index) => {
        const details = car?.details || {};
        const areaPropriedade = details.area_da_propriedade;
        const reservaLegalStatus = classifyReservaLegalStatus(details, sobreposicao);

        return (
          <Accordion key={car?.cod_imovel || index} defaultExpanded={index === 0} sx={{ mb: 2 }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box
                component="table"
                sx={{
                  width: '100%',
                  tableLayout: 'fixed',
                }}
              >
                <Box component="tbody">
                  <Box component="tr">
                    <Box
                      component="td"
                      sx={{
                        width: '36%',
                        pr: 2,
                        verticalAlign: 'top',
                      }}
                    >
                      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                        Código CAR
                      </Typography>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                          mt: 0.25,
                        }}
                      >
                        <Typography
                          variant="body1"
                          sx={{ fontWeight: 600, wordBreak: 'break-all' }}
                        >
                          {car?.cod_imovel || '-'}
                        </Typography>
                        {car?.cod_imovel && (
                          <IconButton
                            size="small"
                            aria-label="Copiar código CAR"
                            onClick={(e) => {
                              e.stopPropagation();
                              navigator.clipboard.writeText(car.cod_imovel).catch(() => {});
                            }}
                          >
                            <ContentCopyIcon fontSize="inherit" />
                          </IconButton>
                        )}
                      </Box>
                    </Box>
                    <Box
                      component="td"
                      sx={{
                        width: '16%',
                        pr: 2,
                        verticalAlign: 'top',
                      }}
                    >
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ fontWeight: 600, display: 'block', textAlign: 'left' }}
                      >
                        Status do Cadastro
                      </Typography>
                      <Box sx={{ mt: 0.5, textAlign: 'left' }}>
                        {(() => {
                          const chipProps = getStatusChipProps(details.situacao_do_cadastro);
                          return <Chip size="small" {...chipProps} />;
                        })()}
                      </Box>
                    </Box>
                    <Box
                      component="td"
                      sx={{
                        width: '16%',
                        pr: 2,
                        verticalAlign: 'top',
                      }}
                    >
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ fontWeight: 600, display: 'block', textAlign: 'left' }}
                      >
                        Adesão ao PRA
                      </Typography>
                      <Box sx={{ mt: 0.5, textAlign: 'left' }}>
                        {details.solicitacao_de_adesao_ao_pra ? (
                          <Chip
                            label={details.solicitacao_de_adesao_ao_pra}
                            size="small"
                            color={
                              String(details.solicitacao_de_adesao_ao_pra).toLowerCase() === 'sim'
                                ? 'success'
                                : 'default'
                            }
                          />
                        ) : (
                          <Typography variant="body2">-</Typography>
                        )}
                      </Box>
                    </Box>
                    <Box
                      component="td"
                      sx={{
                        width: '16%',
                        pr: 2,
                        verticalAlign: 'top',
                      }}
                    >
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ fontWeight: 600, display: 'block', textAlign: 'left' }}
                      >
                        Área da Propriedade
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 600, textAlign: 'left' }}>
                        {formatArea(details.area_da_propriedade)} ha
                      </Typography>
                    </Box>
                    <Box
                      component="td"
                      sx={{
                        width: '16%',
                        verticalAlign: 'top',
                      }}
                    >
                      <Box sx={{ mt: 2, textAlign: 'left' }}>
                        <Chip
                          size="small"
                          label={reservaLegalStatus.label}
                          color={reservaLegalStatus.color}
                          variant={reservaLegalStatus.label === 'Regular' ? 'outlined' : 'filled'}
                        />
                      </Box>
                    </Box>
                  </Box>
                </Box>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Card
                variant="outlined"
                sx={{
                  boxShadow: 1,
                  borderRadius: 2,
                  borderLeft: 4,
                  borderLeftColor: 'success.main',
                  borderLeftStyle: 'solid',
                }}
              >
                <CardContent>
                  {isSectionEnabled('car', 'dados_cadastrais') && (
                    <Box sx={{ mb: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                      <Typography
                        variant="subtitle2"
                        sx={getStyleForRole('sectionTitle', { mb: 1 })}
                      >
                        Dados cadastrais
                      </Typography>
                      <Grid container spacing={2}>
                        {isFieldEnabled('car', 'dados_cadastrais', 'uf') && (
                          <Grid item xs={12} md={1}>
                            {renderField('car', 'uf', getFieldLabel('car', 'uf', 'UF'), details.uf)}
                          </Grid>
                        )}
                        {isFieldEnabled('car', 'dados_cadastrais', 'municipio') && (
                          <Grid item xs={12} md={3}>
                            {renderField(
                              'car',
                              'municipio',
                              getFieldLabel('car', 'municipio', 'Município'),
                              details.municipio,
                            )}
                          </Grid>
                        )}
                        {isFieldEnabled('car', 'dados_cadastrais', 'area_da_propriedade') && (
                          <Grid item xs={12} md={2}>
                            {renderField(
                              'car',
                              'area_da_propriedade',
                              getFieldLabel(
                                'car',
                                'area_da_propriedade',
                                'Área da Propriedade (ha)',
                              ),
                              formatArea(details.area_da_propriedade),
                            )}
                          </Grid>
                        )}
                        {isFieldEnabled('car', 'dados_cadastrais', 'modulos_fiscais') && (
                          <Grid item xs={12} md={2}>
                            {renderField(
                              'car',
                              'modulos_fiscais',
                              getFieldLabel('car', 'modulos_fiscais', 'Módulos Fiscais'),
                              formatArea(details.modulos_fiscais),
                            )}
                          </Grid>
                        )}
                        {isFieldEnabled('car', 'dados_cadastrais', 'tipo_de_imovel_rural') && (
                          <Grid item xs={12} md={2}>
                            {renderField(
                              'car',
                              'tipo_de_imovel_rural',
                              getFieldLabel('car', 'tipo_de_imovel_rural', 'Tipo de Imóvel Rural'),
                              formatTipoImovelLabel(details.tipo_de_imovel_rural),
                            )}
                          </Grid>
                        )}
                        {isFieldEnabled('car', 'dados_cadastrais', 'condicao_do_cadastro') && (
                          <Grid item xs={12} md={2}>
                            {renderField(
                              'car',
                              'condicao_do_cadastro',
                              getFieldLabel('car', 'condicao_do_cadastro', 'Condição do Cadastro'),
                              details.condicao_do_cadastro,
                            )}
                          </Grid>
                        )}
                      </Grid>
                    </Box>
                  )}
                  <Box sx={{ borderTop: 1, borderColor: 'divider', my: 1.5 }} />

                  {isSectionEnabled('car', 'datas') && (
                    <Box sx={{ mb: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                      <Grid container spacing={2}>
                        {isFieldEnabled('car', 'datas', 'data_de_inscricao') && (
                          <Grid item xs={12} md={3}>
                            {renderField(
                              'car',
                              'data_de_inscricao',
                              getFieldLabel('car', 'data_de_inscricao', 'Data da Inscrição'),
                              formatDateBr(details.data_de_inscricao),
                            )}
                          </Grid>
                        )}
                        {isFieldEnabled('car', 'datas', 'data_da_ultima_retificacao') && (
                          <Grid item xs={12} md={3}>
                            {renderField(
                              'car',
                              'data_da_ultima_retificacao',
                              getFieldLabel(
                                'car',
                                'data_da_ultima_retificacao',
                                'Última Retificação',
                              ),
                              formatDateBr(details.data_da_ultima_retificacao),
                            )}
                          </Grid>
                        )}
                        {isFieldEnabled(
                          'car',
                          'datas',
                          'data_de_alteracao_da_condicao_do_cadastro',
                        ) && (
                          <Grid item xs={12} md={3}>
                            {renderField(
                              'car',
                              'data_de_alteracao_da_condicao_do_cadastro',
                              getFieldLabel(
                                'car',
                                'data_de_alteracao_da_condicao_do_cadastro',
                                'Alteração da Condição do Cadastro',
                              ),
                              formatDateBr(details.data_de_alteracao_da_condicao_do_cadastro),
                            )}
                          </Grid>
                        )}
                        <Grid item xs={12} md={3}>
                          <Box sx={{ mb: 1.5 }}>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}
                            >
                              Bioma
                            </Typography>
                            {biomasChips.length === 0 ? (
                              <Typography variant="body1" sx={{ fontWeight: 500 }}>
                                -
                              </Typography>
                            ) : (
                              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                {biomasChips[0] && (
                                  <Chip
                                    size="small"
                                    label={formatBiomaChipLabel(
                                      biomasChips[0].bioma,
                                      biomasChips[0].pct,
                                    )}
                                    variant="outlined"
                                    color="default"
                                  />
                                )}
                                {biomasChips[1] && (
                                  <Chip
                                    size="small"
                                    label={formatBiomaChipLabel(
                                      biomasChips[1].bioma,
                                      biomasChips[1].pct,
                                    )}
                                    variant="outlined"
                                    color="default"
                                  />
                                )}
                                {biomasChips[2] && (
                                  <Chip
                                    size="small"
                                    label={formatBiomaChipLabel(
                                      biomasChips[2].bioma,
                                      biomasChips[2].pct,
                                    )}
                                    variant="outlined"
                                    color="default"
                                  />
                                )}
                              </Box>
                            )}
                          </Box>
                        </Grid>
                      </Grid>
                    </Box>
                  )}
                  <Box sx={{ borderTop: 1, borderColor: 'divider', my: 1.5 }} />

                  {isSectionEnabled('car', 'temas_ambientais') &&
                    (visibleLeftThemes.length > 0 || visibleRightThemes.length > 0) && (
                      <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                        <Grid container spacing={2}>
                          {visibleLeftThemes.length > 0 && (
                            <Grid item xs={12} md={6}>
                              {renderThemesTable(visibleLeftThemes, details, areaPropriedade)}
                            </Grid>
                          )}
                          {visibleRightThemes.length > 0 && (
                            <Grid item xs={12} md={6}>
                              {renderThemesTable(visibleRightThemes, details, areaPropriedade)}
                            </Grid>
                          )}
                        </Grid>
                      </Box>
                    )}
                </CardContent>
              </Card>
            </AccordionDetails>
          </Accordion>
        );
      })}
    </Box>
  );
}
